import React from "react";
import { Link } from "react-router-dom";
import { useLabel } from "@/lib/dynamicEngine";
import { useNexOs } from "@/lib/nexOs";
import { useDemoMode } from "@/lib/demoMode";
import { MousePointerClick, PlayCircle, Cpu, Download, Globe, Home } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import SystemConsoleMenu from "@/components/nds/SystemConsoleMenu";

/**
 * Tier 0 — Global Header.
 * Brand anchor · LIVE indicator · LIVE/SANDBOX mode toggle · admin links.
 */
export default function NdsHeader() {
  const { schema, mode, setMode, vertical } = useNexOs();
  const demoMode = useDemoMode();
  const brand = schema?.brand || {};
  // Wordmark is hardcoded to the /demo-aligned brand so the operator console
  // matches the prospect surface without a schema-labels migration.
  const wordmark = "NEXENGINES";
  const statusLabel = useLabel("nds.header.status", "LIVE");
  const envLabel = useLabel("nds.header.env", (brand.environment || "production").toUpperCase());
  const opsLabel = useLabel("nds.header.ops", "OPERATING SYSTEM · NDS-1");

  const onExport = async () => {
    if (demoMode) {
      toast.info("Demo · export runs against your real engine after signup");
      return;
    }
    try {
      const [{ data: sc }, { data: kpi }] = await Promise.all([
        apiClient.get("/nex-os/schema"),
        apiClient.get(`/nex-os/kpis?vertical=${vertical || "all_engines"}&mode=${mode}`),
      ]);
      const caps = sc?.schema?.capabilities_template || [];
      const now  = new Date().toISOString();
      const text =
        `NEXENGINES · NDS-1 · Operator Audit Export\n` +
        `==========================================\n` +
        `Exported: ${now}\n` +
        `Vertical: ${vertical || "all_engines"}   Mode: ${mode}\n\n` +
        `KPIs\n----\n` +
        (kpi?.kpis || []).map((k) => `• ${k.label}: ${k.value}`).join("\n") + "\n\n" +
        `Capabilities (${caps.length})\n----\n` +
        caps.map((c) => `• [T${c.build_tier}·${c.est_credits}cr] ${c.label} — ${c.status}`).join("\n");
      const blob = new Blob([text], { type: "text/plain" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `Nexengines_Operator_Audit_${new Date().toISOString().slice(0,10)}.txt`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Operator audit downloaded");
    } catch (e) {
      toast.error("Export failed — try again in a moment");
    }
  };

  return (
    <header
      data-testid="nds-header"
      className="w-full border-b border-white/8 bg-navy-900/90 backdrop-blur-md sticky top-0 z-40"
    >
      <div className="w-full max-w-[1920px] mx-auto flex items-center justify-between gap-4 px-4 md:px-6 lg:px-10 py-4 flex-wrap">
        {/* Brand — blue→cyan→emerald gradient CPU tile + wordmark + version pill */}
        <Link to={demoMode ? "/demo" : "/os"} data-testid="nds-brand-link" className="flex items-center gap-3 group min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-brand-blue via-brand-cyan to-brand-emerald flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Cpu className="w-6 h-6 text-navy-950" aria-hidden="true" />
          </div>
          <div className="flex flex-col leading-tight">
            <div className="flex items-center gap-2">
              <span className="text-lg font-extrabold tracking-tight text-white">
                {wordmark}
              </span>
              <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-brand-blue/10 text-brand-cyan border border-brand-blue/30">
                v4.8 enterprise
              </span>
            </div>
            <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-slate-400 mt-0.5">{opsLabel}{demoMode ? " · PROSPECT DEMO" : ""}</span>
          </div>
        </Link>

        {/* Right rail */}
        <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
          {demoMode ? (
            <div
              className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono text-[0.6rem] tracking-[0.14em] uppercase"
              data-testid="nds-demo-hint"
              title="This is a live sandbox — try any card. Nothing you type gets saved."
            >
              <PlayCircle className="w-3 h-3" /> Live sandbox · click any card
            </div>
          ) : (
            <div
              className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-brand-blue/30 bg-brand-blue/10 text-brand-cyan font-mono text-[0.6rem] tracking-[0.14em] uppercase"
              data-testid="nds-edit-hint"
              title="Right-click any tile to edit its schema"
            >
              <MousePointerClick className="w-3 h-3" /> Right-click to edit
            </div>
          )}
          {/* LIVE / SANDBOX toggle — restyled with cyan-blue gradient */}
          {!demoMode && (
            <div
              className="inline-flex items-center bg-navy-950 p-1 rounded-xl border border-slate-800"
              data-testid="nds-mode-toggle"
            >
              {["live", "sandbox"].map((m) => {
                const active = m === mode;
                return (
                  <button
                    key={m}
                    type="button"
                    data-testid={`nds-mode-${m}`}
                    data-active={active}
                    onClick={() => setMode(m)}
                    className={
                      active
                        ? "flex items-center space-x-2 px-3 md:px-4 py-1.5 rounded-lg text-[0.65rem] md:text-xs font-mono font-medium tracking-[0.18em] uppercase transition-all duration-200 bg-gradient-to-r from-brand-blue to-brand-cyan text-white shadow-md"
                        : "flex items-center space-x-2 px-3 md:px-4 py-1.5 rounded-lg text-[0.65rem] md:text-xs font-mono font-medium tracking-[0.18em] uppercase transition-all duration-200 text-slate-300 hover:text-white"
                    }
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}

          <div
            className="flex items-center gap-1.5 px-2 md:px-2.5 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10"
            data-testid="nds-live-indicator"
          >
            <span className="nds-live-dot" />
            <span className="font-mono text-[0.6rem] md:text-[0.7rem] tracking-[0.14em] md:tracking-[0.24em] text-emerald-400 uppercase">
              {statusLabel}
            </span>
          </div>
          <div
            className="hidden sm:inline-flex px-2.5 py-1.5 rounded-lg border border-slate-800 bg-navy-950 font-mono text-[0.65rem] md:text-[0.7rem] tracking-[0.14em] md:tracking-[0.24em] text-slate-400 uppercase"
            data-testid="nds-env-badge"
          >
            ENV · {demoMode ? "DEMO" : envLabel}
          </div>
          {!demoMode && (
            <button
              type="button"
              onClick={onExport}
              data-testid="nds-export-audit"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-blue/20 hover:bg-brand-blue/30 text-brand-cyan border border-brand-blue/40 text-[0.65rem] md:text-xs font-mono font-medium tracking-[0.14em] uppercase transition"
              title="Export live KPIs + capability roster as .txt"
            >
              <Download className="w-3.5 h-3.5" /> Export Audit
            </button>
          )}
          {demoMode ? (
            <>
              <Link
                to="/"
                data-testid="nds-demo-home-link"
                title="Back to Nexengines home"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white border border-white/10 hover:border-white/30 text-[0.65rem] md:text-xs font-mono font-medium tracking-[0.14em] uppercase transition"
              >
                <Home className="w-3.5 h-3.5" /> Home
              </Link>
              <Link
                to="/#friction-audit"
                data-testid="nds-demo-cta"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan text-white font-mono text-[0.6rem] md:text-[0.7rem] tracking-[0.14em] md:tracking-[0.24em] uppercase shadow-lg shadow-cyan-500/20 transition"
              >
                Book Your Engine
              </Link>
            </>
          ) : (
            <>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nds-website-link"
                title="Open the public marketing site in a new tab"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:border-emerald-400/60 text-[0.65rem] md:text-xs font-mono font-medium tracking-[0.14em] uppercase transition"
              >
                <Globe className="w-3.5 h-3.5" /> Website
              </a>
              <SystemConsoleMenu />
            </>
          )}
        </div>
      </div>
    </header>
  );
}
